import {
    GoogleMap,
    LoadScriptProps,
    Marker,
    useJsApiLoader,
} from '@react-google-maps/api';
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { Place } from '@barboxx/models';

import { REACT_APP_GOOGLE_MAPS_API_KEY } from '../../../../../environments/environment';
import { AutoCompleteRefProps, GooglePlaceSearch } from './GooglePlaceSearch';

import './mapStyles.scss';

import { LocalPlaceMarker } from './LocalPlaceMarker';

const BASE_CENTER: google.maps.LatLngLiteral = {
    lat: 30.3321838,
    lng: -81.65565,
};

const GOOGLE_PLACE_LIBRARIES: LoadScriptProps['libraries'] = ['places'];
interface Props {
    onBusinessSelect: (data: google.maps.places.PlaceResult) => void;
    center?: google.maps.LatLngLiteral;
    onSelectPlace?: (id: string) => void;
    possiblePlaces?: Place[];
    searchForPossiblePlaces?: (latLng: google.maps.LatLngLiteral) => void;
}

export interface SearchMapRefProps {
    clear: () => void;
}

const GOOGLE_MAPS_API_KEY = REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
export const SearchMap = forwardRef<SearchMapRefProps, Props>(
    function SearchMap(
        {
            possiblePlaces = [],
            searchForPossiblePlaces,
            onBusinessSelect,
            center = BASE_CENTER,
            onSelectPlace,
        },
        ref
    ) {
        useImperativeHandle(ref, () => ({
            clear,
        }));

        const mapRef = useRef<google.maps.Map | null>(null);
        const { isLoaded } = useJsApiLoader({
            id: 'google-map-script',
            googleMapsApiKey: GOOGLE_MAPS_API_KEY,
            libraries: GOOGLE_PLACE_LIBRARIES,
        });
        const autoCompleteRef = useRef<AutoCompleteRefProps | null>(null);
        // const [map, setMap] = useState<google.maps.Map | null>(null);
        const [place, updatePlace] = useState<
            google.maps.places.PlaceResult | undefined
        >();

        const onLoad = useCallback(
            (map: google.maps.Map) => {
                if (center && !place) {
                    map.setCenter(center);
                    map.setZoom(13);
                }

                // setMap(map);

                mapRef.current = map;
            },
            [center, place]
        );

        const onUnmount = useCallback(function callback(map: google.maps.Map) {
            // setMap(null);
            mapRef.current = null;
        }, []);

        const onPlaceChanged = async (data: google.maps.places.PlaceResult) => {
            onBusinessSelect(data);
            const { location } = data.geometry ?? {};

            if (location) {
                // map?.setCenter(location);
                // map?.setZoom(17);
                updatePlace(data);

                mapRef.current?.setCenter(location);
                mapRef.current?.setZoom(17);

                const latLng = location.toJSON();
                searchForPossiblePlaces && searchForPossiblePlaces(latLng);
            }
        };

        const clear = () => {
            autoCompleteRef.current?.clear();
            updatePlace(undefined);
        };

        if (!isLoaded) {
            return null;
        }

        return (
            <GoogleMap
                mapContainerClassName="mapBox"
                center={center}
                zoom={11}
                onLoad={onLoad}
                onUnmount={onUnmount}
                // ref={mapRef}
                options={{
                    mapId: '24de73c1026e9fda',
                    disableDefaultUI: true,
                    backgroundColor: 'black',
                }}
            >
                <GooglePlaceSearch
                    onPlaceSelect={onPlaceChanged}
                    ref={autoCompleteRef}
                />
                {place && (
                    <Marker
                        position={
                            place.geometry?.location as google.maps.LatLng
                        }
                        onClick={() => {
                            onPlaceChanged(place);
                        }}
                    />
                )}
                {possiblePlaces?.map(p => (
                    <LocalPlaceMarker
                        key={p.id}
                        place={p}
                        onSelect={onSelectPlace}
                    />
                ))}
            </GoogleMap>
        );
    }
);
